import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { api, errorMessage } from "../api/client.ts";
import type { PreparedCall, VaultAction, VaultData, VaultItem } from "../api/types.ts";
import { Dialog } from "../components/Dialog.tsx";
import { IconExternal } from "../components/Icons.tsx";
import { Alert, CopyButton, Hash, Spinner } from "../components/ui.tsx";
import { fmt, rich, useT } from "../i18n/index.tsx";
import { chainName, formatUnits, parseUnits, shorten } from "../lib/format.ts";
import { connectOwner, gasToken, hasWallet, isTron, sendCall, txExplorerUrl, walletInstallUrl, WalletError } from "../lib/wallet.ts";

const addressOk = (chain: string, v: string) => (isTron(chain) ? /^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(v) : /^0x[0-9a-fA-F]{40}$/.test(v));
/** Human amount for an input field: "1,234.50" → "1234.5". */
const plain = (raw: string | null, decimals: number) => (raw === null ? "" : formatUnits(raw, decimals, 0, decimals).replace(/,/g, ""));

/** Quick picks for vault payout limits, in whole tokens. */
export const PER_TX_PRESETS = ["10000", "50000", "100000"];
export const DAILY_PRESETS = ["500000", "1000000", "5000000"];

/** A whole-token amount input with quick-pick buttons (used for vault limits). */
export function LimitField({ id, label, value, onChange, token, presets, error, hint }: {
  id: string; label: string; value: string; onChange: (v: string) => void; token: string; presets: string[]; error?: string | null; hint?: ReactNode;
}) {
  const t = useT();
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <div className="input-affix">
        <input id={id} inputMode="decimal" autoComplete="off" value={value} onChange={(e) => onChange(e.target.value)} aria-invalid={!!error} placeholder="0" />
        <span className="affix">{token}</span>
      </div>
      <div className="seg presets" role="group" aria-label={`${label}: ${t.vault.networks.quickPicks}`}>
        {presets.map((p) => (
          <button key={p} type="button" aria-pressed={value.replace(/,/g, "") === p} onClick={() => onChange(p)}>
            {Number(p).toLocaleString("en")}
          </button>
        ))}
      </div>
      {error ? <p className="field-error">{error}</p> : hint && <p className="field-hint">{hint}</p>}
    </div>
  );
}

type Step = 1 | 2 | 3 | 4;
type WalletProblem = { kind: WalletError["kind"]; account?: string; message?: string } | null;

/**
 * Owner action on the vault, as four plain steps:
 * 1 what to change (prepared by the server) → 2 open the owner wallet (connect, owner check, network switch)
 * → 3 confirm in the wallet (network fee note) → 4 wait until the vault shows the new value.
 * Without a wallet, step 2 explains what to install and shows the raw call for a multisig.
 */
export function VaultActionDialog({ vault: v, action, onClose, onSent }: { vault: VaultItem; action: VaultAction; onClose: () => void; onSent: () => void }) {
  const t = useT();
  const g = t.vault.guide;
  const [step, setStep] = useState<Step>(1);
  const [perTx, setPerTx] = useState(plain(v.per_tx_limit === "0" ? null : v.per_tx_limit, v.decimals) || "50000");
  const [daily, setDaily] = useState(plain(v.daily_limit === "0" ? null : v.daily_limit, v.decimals) || "1000000");
  const [amount, setAmount] = useState(action === "setFloat" ? plain(v.float, v.decimals) : "");
  const [address, setAddress] = useState(action === "withdraw" ? v.owner ?? "" : action === "setColdWallet" ? v.cold_wallet ?? "" : "");
  const [call, setCall] = useState<PreparedCall | null>(null);
  const [target, setTarget] = useState<Record<string, string>>({});
  const [summary, setSummary] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ReactNode>(null);
  const [problem, setProblem] = useState<WalletProblem>(null);
  const [account, setAccount] = useState("");
  const [tx, setTx] = useState("");
  const [done, setDone] = useState(false);
  const [slow, setSlow] = useState(false);
  const [, setTick] = useState(0); // re-render after "Check again"
  const tok = v.token;
  const vaultShort = shorten(v.vault_address);
  const network = chainName(v.chain);
  const gas = gasToken(v.chain);

  const amountErr = (s: string) => (s.trim() && !parseUnits(s, v.decimals) ? fmt(t.vault.amountInvalid, { decimals: v.decimals }) : null);
  const wRaw = action === "withdraw" ? parseUnits(amount, v.decimals) : null;
  const overAvailable = wRaw !== null && BigInt(wRaw) > BigInt(v.available || "0");
  const belowPending = wRaw !== null && v.balance !== null && BigInt(v.balance) - BigInt(wRaw) < BigInt(v.pending_payouts || "0");

  // ---- Step 1: what to change → server encodes the call ----
  const prepare = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    let body: Record<string, string> = { action };
    let text = "";
    const fmtAmt = (raw: string) => formatUnits(raw, v.decimals);
    if (action === "setLimits") {
      const a = parseUnits(perTx, v.decimals);
      const b = parseUnits(daily, v.decimals);
      if (!a || !b) return setError(fmt(t.vault.amountInvalid, { decimals: v.decimals }));
      if (BigInt(b) < BigInt(a)) return setError(t.vault.dailyBelowPerTx);
      body = { action, per_tx: a, daily: b };
      text = fmt(t.vault.summary.setLimits, { perTx: fmtAmt(a), daily: fmtAmt(b), token: tok, vault: vaultShort });
    } else if (action === "setFloat") {
      const a = parseUnits(amount, v.decimals);
      if (!a) return setError(fmt(t.vault.amountInvalid, { decimals: v.decimals }));
      body = { action, amount: a };
      text = fmt(t.vault.summary.setFloat, { amount: fmtAmt(a), token: tok, vault: vaultShort });
    } else if (action === "setColdWallet") {
      if (!addressOk(v.chain, address.trim())) return setError(fmt(t.vault.addressInvalid, { chain: network }));
      body = { action, address: address.trim() };
      text = fmt(t.vault.summary.setColdWallet, { vault: vaultShort, address: address.trim() });
    } else if (action === "withdraw") {
      const a = parseUnits(amount, v.decimals);
      if (!a || a === "0") return setError(fmt(t.vault.amountInvalid, { decimals: v.decimals }));
      if (!addressOk(v.chain, address.trim())) return setError(fmt(t.vault.addressInvalid, { chain: network }));
      body = { action, to: address.trim(), amount: a };
      text = fmt(t.vault.summary.withdraw, { amount: fmtAmt(a), token: tok, vault: vaultShort, address: address.trim() });
    } else if (action === "sweepToCold") {
      if (!v.cold_wallet) return setError(t.vault.sweepNeedsCold);
      text = fmt(t.vault.summary.sweepToCold, { vault: vaultShort, address: v.cold_wallet });
    } else {
      text = fmt(t.vault.summary[action], { vault: vaultShort });
    }
    setBusy(true);
    try {
      setCall(await api.post<PreparedCall>(`/vault/${encodeURIComponent(v.asset)}/prepare`, body));
      setTarget(body);
      setSummary(text);
      setProblem(null);
      setStep(2);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  // ---- Step 2: open the wallet, owner check, network switch ----
  const connect = async () => {
    if (!call) return;
    setBusy(true);
    setProblem(null);
    try {
      setAccount(await connectOwner(call));
      setStep(3);
    } catch (err) {
      const w = err instanceof WalletError ? err : new WalletError("failed", String(err));
      setProblem({ kind: w.kind, account: w.account, message: w.message });
    } finally {
      setBusy(false);
    }
  };

  // ---- Step 3: confirm in the wallet ----
  const send = async () => {
    if (!call) return;
    setBusy(true);
    setProblem(null);
    try {
      setTx(await sendCall(call, account));
      setStep(4);
    } catch (err) {
      const w = err instanceof WalletError ? err : new WalletError("failed", String(err));
      // A different account or network since step 2: go back to the wallet check.
      if (w.kind === "wrong_account" || w.kind === "wrong_network" || w.kind === "no_wallet") setStep(2);
      setProblem({ kind: w.kind, account: w.account, message: w.message });
    } finally {
      setBusy(false);
    }
  };

  // ---- Step 4: wait until the vault snapshot shows the change (refreshed about once a minute) ----
  const before = useRef(v);
  useEffect(() => {
    if (step !== 4 || done) return;
    const started = Date.now();
    const applied = (x: VaultItem) => {
      const b = before.current;
      switch (action) {
        case "setLimits": return x.per_tx_limit === target.per_tx && x.daily_limit === target.daily;
        case "setFloat": return x.float === target.amount;
        case "setColdWallet": return (x.cold_wallet ?? "").toLowerCase() === (target.address ?? "").toLowerCase();
        case "pause": return x.paused === true;
        case "unpause": return x.paused === false;
        case "withdraw":
        case "sweepToCold": return x.balance !== null && b.balance !== null && BigInt(x.balance) < BigInt(b.balance);
      }
    };
    const check = async () => {
      try {
        const d = await api.get<VaultData>("/vault");
        const x = d.items.find((i) => i.asset === v.asset);
        if (x && applied(x)) {
          setDone(true);
          onSent();
          return;
        }
      } catch {
        /* keep waiting */
      }
      if (Date.now() - started > 5 * 60_000) setSlow(true);
    };
    const timer = setInterval(() => void check(), 6000);
    return () => clearInterval(timer);
  }, [step, done, action, target, v.asset, onSent]);

  const problemText = (p: NonNullable<WalletProblem>): ReactNode => {
    switch (p.kind) {
      case "wrong_account":
        return (
          <div className="wallet-problem">
            <strong>{g.wrongAccountTitle}</strong>
            <p>{g.wrongAccountBody}</p>
            <div className="owner-address"><code>{call?.owner}</code><CopyButton value={call?.owner ?? ""} label={t.common.copy} /></div>
            {p.account && <p className="muted small">{fmt(g.connectedNow, { account: p.account })}</p>}
          </div>
        );
      case "wrong_network": return fmt(g.wrongNetwork, { network });
      case "no_account": return g.noAccount;
      case "rejected": return step === 3 ? g.rejectedSend : g.rejectedConnect;
      case "insufficient_gas": return fmt(g.insufficientGas, { gas });
      case "no_wallet": return g.noWalletTitle;
      default: return fmt(g.sendFailed, { message: p.message ?? "" });
    }
  };

  const steps = [g.stepWhat, g.stepWallet, g.stepConfirm, g.stepWaiting];
  const explorer = txExplorerUrl(v.vault_url, v.chain, tx);

  return (
    <Dialog open onClose={onClose} busy={busy} title={t.vault.actions[action]}
      description={fmt(t.vault.dialogIntro, { vault: vaultShort, owner: v.owner ? shorten(v.owner) : t.vault.unknown })}>
      <ol className="steps-mini guide-steps" aria-label={g.progress}>
        {steps.map((label, i) => {
          const n = (i + 1) as Step;
          const state = n < step || (n === 4 && done) ? "done" : n === step ? "current" : "";
          return (
            <li key={label} className={state} aria-current={n === step ? "step" : undefined}>
              <span>{n}</span> {label}
            </li>
          );
        })}
      </ol>

      {step === 1 && (
        <form className="form-stack" onSubmit={prepare} noValidate>
          {action === "setLimits" && (
            <>
              <LimitField id="va-pertx" label={t.vault.perTxField} value={perTx} onChange={setPerTx} token={tok} presets={PER_TX_PRESETS} error={amountErr(perTx)}
                hint={fmt(t.vault.currentValue, { value: v.per_tx_limit === null ? t.vault.notSet : `${formatUnits(v.per_tx_limit, v.decimals)} ${tok}` })} />
              <LimitField id="va-daily" label={t.vault.dailyField} value={daily} onChange={setDaily} token={tok} presets={DAILY_PRESETS} error={amountErr(daily)}
                hint={fmt(t.vault.currentValue, { value: v.daily_limit === null ? t.vault.notSet : `${formatUnits(v.daily_limit, v.decimals)} ${tok}` })} />
              <p className="field-hint">{t.vault.limitsCountFee}</p>
            </>
          )}
          {action === "setFloat" && (
            <div className="field">
              <label htmlFor="va-float">{t.vault.floatField}</label>
              <div className="input-affix">
                <input id="va-float" inputMode="decimal" autoComplete="off" value={amount} onChange={(e) => setAmount(e.target.value)} aria-invalid={!!amountErr(amount)} placeholder="0" />
                <span className="affix">{tok}</span>
              </div>
              <p className={amountErr(amount) ? "field-error" : "field-hint"}>{amountErr(amount) ?? t.vault.floatFieldHint}</p>
            </div>
          )}
          {(action === "setColdWallet" || action === "withdraw") && (
            <div className="field">
              <label htmlFor="va-addr">{action === "withdraw" ? t.vault.withdrawTo : t.vault.coldWalletField}</label>
              <input id="va-addr" className="mono-input" value={address} onChange={(e) => setAddress(e.target.value)} spellCheck={false} autoComplete="off"
                placeholder={isTron(v.chain) ? "T…" : "0x…"} />
            </div>
          )}
          {action === "withdraw" && (
            <>
              <div className="field">
                <label htmlFor="va-amount">{t.vault.withdrawAmount}</label>
                <div className="input-affix">
                  <input id="va-amount" inputMode="decimal" autoComplete="off" value={amount} onChange={(e) => setAmount(e.target.value)} aria-invalid={!!amountErr(amount)} placeholder="0.00" />
                  <span className="affix">{tok}</span>
                </div>
                {amountErr(amount) && <p className="field-error">{amountErr(amount)}</p>}
              </div>
              {overAvailable && <Alert tone="warn">{fmt(t.vault.withdrawWarnAvailable, { available: formatUnits(v.available, v.decimals), token: tok })}</Alert>}
              {belowPending && <Alert tone="warn">{fmt(t.vault.withdrawWarnPending, { pending: formatUnits(v.pending_payouts, v.decimals), token: tok })}</Alert>}
            </>
          )}
          {action === "sweepToCold" && !v.cold_wallet && <Alert tone="warn">{t.vault.sweepNeedsCold}</Alert>}
          {(action === "pause" || action === "unpause" || action === "sweepToCold") && <p className="vault-summary">{fmt(t.vault.summary[action], { vault: vaultShort, address: v.cold_wallet ?? "" })}</p>}
          {error && <Alert tone="error">{error}</Alert>}
          <p className="field-hint">{t.vault.neverKeys}</p>
          <div className="dlg-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>{t.common.cancel}</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? <><Spinner /> {t.vault.preparing}</> : t.common.continue}</button>
          </div>
        </form>
      )}

      {step === 2 && call && (
        <div className="form-stack">
          <p className="vault-summary">{summary}</p>
          {!hasWallet(call.chain) ? (
            <div className="wallet-missing">
              <strong>{g.noWalletTitle}</strong>
              <p>
                <a href={walletInstallUrl(call.chain)} target="_blank" rel="noreferrer noopener" className="ext-link">
                  {isTron(call.chain) ? g.installTron : fmt(g.installEvm, { network })} <IconExternal size={14} />
                </a>
              </p>
              <p className="muted">{g.openOnOwnerComputer}</p>
              <RawCall call={call} label={g.manualCall} />
            </div>
          ) : (
            <p>{g.walletIntro}</p>
          )}
          {problem && (problem.kind === "wrong_account" ? problemText(problem) : <Alert tone="error">{problemText(problem)}</Alert>)}
          <div className="dlg-actions">
            <button type="button" className="btn btn-ghost" onClick={() => { setStep(1); setProblem(null); }} disabled={busy}>{t.common.back}</button>
            {!hasWallet(call.chain) ? (
              <button type="button" className="btn btn-primary" onClick={() => setTick((n) => n + 1)}>{g.checkAgain}</button>
            ) : (
              <button type="button" className="btn btn-primary" onClick={() => void connect()} disabled={busy}>
                {busy ? <><Spinner /> {g.connecting}</> : problem ? t.common.retry : g.connect}
              </button>
            )}
          </div>
        </div>
      )}

      {step === 3 && call && (
        <div className="form-stack">
          <ul className="check-list">
            <li>{fmt(g.connected, { account: shorten(account) })} · {g.ownerOk}</li>
            {!isTron(call.chain) && <li>{fmt(g.networkOk, { network })}</li>}
          </ul>
          <p className="vault-summary">{summary}</p>
          <p>{fmt(g.confirmWhat, { vault: vaultShort })}</p>
          <p className="fee-note">{fmt(g.feeNote, { gas })}</p>
          {problem && <Alert tone="error">{problemText(problem)}</Alert>}
          <div className="dlg-actions">
            <button type="button" className="btn btn-ghost" onClick={() => { setStep(2); setProblem(null); }} disabled={busy}>{t.common.back}</button>
            <button type="button" className="btn btn-primary" onClick={() => void send()} disabled={busy}>
              {busy ? <><Spinner /> {t.vault.signing}</> : problem ? t.common.retry : g.openWallet}
            </button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="form-stack">
          {done ? (
            <Alert tone="success" title={g.done[action]} />
          ) : (
            <>
              <p>{g.waitingIntro}</p>
              <p className="waiting-line"><Spinner /> {g.checking}</p>
              {slow && <p className="muted small">{g.stillWaiting}</p>}
            </>
          )}
          <div className="field">
            <span className="field-label">{t.vault.txHash}</span>
            <Hash value={tx} href={explorer} full />
            {explorer && <a href={explorer} target="_blank" rel="noreferrer noopener" className="ext-link small">{t.vault.viewOnExplorer} <IconExternal size={14} /></a>}
          </div>
          <div className="dlg-actions">
            <button type="button" className="btn btn-primary" onClick={onClose} data-autofocus>{done ? t.common.done : t.common.close}</button>
          </div>
        </div>
      )}

      <p className="guide-help muted small">
        {rich(g.help, { a: (c) => <a href="https://t.me/mrho68" target="_blank" rel="noreferrer noopener">{c}</a> })}
      </p>
    </Dialog>
  );
}

/** The encoded call, for a wallet-less or multisig submission. */
function RawCall({ call, label }: { call: PreparedCall; label: string }) {
  const t = useT();
  const rows: [string, string][] = [
    [t.vault.rawTo, call.to],
    [t.vault.rawFunction, call.function],
    [t.vault.rawArgs, call.args.join(", ") || "–"],
    ...(isTron(call.chain)
      ? ([[t.vault.rawSelector, call.function_selector], [t.vault.rawParameter, call.raw_parameter]] as [string, string][])
      : ([[t.vault.rawData, call.data]] as [string, string][])),
  ];
  return (
    <details className="raw-call">
      <summary>{label}</summary>
      <dl>
        {rows.map(([k, val]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd><code>{val}</code>{val !== "–" && <CopyButton value={val} label={t.common.copy} />}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
}
